import { useState } from "react";
import Modal from "../../components/common/Modal";
import { FormikHelpers, useFormik } from "formik";
import { RoleInterface, TRoleFilter } from "../../interfaces/roleInterface";
import { usePagination } from "../../components/common/data-table/hooks/usePagination";
import { ListSearchControls } from "../../components/common/data-table/ListSearchControls";
import { ListViewControls } from "../../components/common/data-table/ListViewControls";
import Button from "../../components/common/Buttons/Button";
import { faCheck, faEdit, faXmark } from "@fortawesome/free-solid-svg-icons";
import {
  roleSchema,
  useRoles,
  useSaveRole,
  useRoleStatusToggle,
  useRoleByIdQuery,
  RoleForm,
  RoleTable,
  RoleHeader,
} from "../../features/roles";

const formDataInit: RoleInterface = {
  name: "",
  permissions: {
    users: { create: false, read: false, update: false, delete: false },
    roles: { create: false, read: false, update: false, delete: false },
    categories: { create: false, read: false, update: false, delete: false },
    products: { create: false, read: false, update: false, delete: false },
    branches: { create: false, read: false, update: false, delete: false },
    dealers: { create: false, read: false, update: false, delete: false },
    shippingCosts: {
      create: false,
      read: false,
      update: false,
      delete: false,
    },
    orders: { create: false, read: false, update: false, delete: false },
    promotions: { create: false, read: false, update: false, delete: false },
  },
};

const filterInit: TRoleFilter = {};

const RolesPage = () => {
  const [showModal, setShowModal] = useState<boolean>(false);
  const [editingRoleId, setEditingRoleId] = useState<string | null>(null);
  const { pagination, handlePageChange, handlePageSizeChange, handleSort } =
    usePagination({
      initialPageSize: 10,
      initialSortBy: "name",
      initialSortOrder: "desc",
    });

  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState<TRoleFilter>(filterInit);

  const handleSearch = () => {
    setFilter({ ...filterInit, search: searchTerm || undefined });
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setFilter(filterInit);
  };

  const { roles, paginationData, isLoading } = useRoles({
    filter,
    pagination,
  });

  const { data: roleToEdit } = useRoleByIdQuery(editingRoleId ?? undefined);

  const { handleSave } = useSaveRole();
  const { handleEnable, handleDisable } = useRoleStatusToggle();

  const formik = useFormik<RoleInterface>({
    initialValues: roleToEdit?.data || formDataInit,
    validationSchema: roleSchema,
    enableReinitialize: true,
    onSubmit: (
      values: RoleInterface,
      formikHelpers: FormikHelpers<RoleInterface>,
    ) => {
      handleSave(values);
      formikHelpers.setSubmitting(false);
      formikHelpers.resetForm({ values: formDataInit });
      handleCloseModal();
    },
  });

  const handleOpenNewRoleModal = () => {
    formik.resetForm({ values: formDataInit });
    setEditingRoleId(null);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    formik.resetForm({ values: formDataInit });
    setEditingRoleId(null);
    setShowModal(false);
  };

  const handleEditRole = (role: RoleInterface) => {
    const { _id: roleId } = role;
    if (!roleId) return;
    setEditingRoleId(roleId);
    setShowModal(true);
  };

  return (
    <div>
      <RoleHeader />
      <ListSearchControls
        searchLabel="Buscar por nombre"
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        onSearch={handleSearch}
        onClear={handleClearSearch}
      />
      <ListViewControls
        pageSize={pagination.pageSize}
        onPageSizeChange={handlePageSizeChange}
      >
        <Button size="medium" onClick={handleOpenNewRoleModal} color="primary">
          Nuevo
        </Button>
      </ListViewControls>
      <RoleTable
        data={roles}
        isLoading={isLoading}
        sortBy={pagination.sortBy as keyof RoleInterface}
        sortOrder={pagination.sortOrder}
        onSort={handleSort}
        pagination={{
          currentPage: paginationData?.currentPage ?? 1,
          totalPages: paginationData?.totalPages ?? 0,
          pageSize: paginationData?.pageSize ?? 10,
          total: paginationData?.total ?? 0,
          onPageChange: handlePageChange,
        }}
        extraActions={[
          {
            title: "Editar",
            icon: faEdit,
            actionKey: "edit",
            onClick: handleEditRole,
          },
          {
            title: "Deshabilitar",
            icon: faXmark,
            actionKey: "disable",
            color: "secondary",
            onClick: (row) => handleDisable(row._id!),
            shouldShow: (row) => !!row.active,
          },
          {
            title: "Habilitar",
            icon: faCheck,
            actionKey: "enable",
            color: "success",
            onClick: (row) => handleEnable(row._id!),
            shouldShow: (row) => !row.active,
          },
        ]}
      />
      <Modal show={showModal} close={handleCloseModal} submit={formik.handleSubmit}>
        <RoleForm formik={formik} />
      </Modal>
    </div>
  );
};

export default RolesPage;
